import { PropsWithChildren, useRef, DOMAttributes } from 'react';
import A11yDialogInstance from 'a11y-dialog';
import { A11yDialog } from 'react-a11y-dialog';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import type { ReactElement } from 'react';

import './Dialog.scss';
import { getUniqueId, getBreakpoint } from '@micro-apps/env';
import Icon from '../Icon';
import { noop } from 'lodash';
type A11yDialogClassNames = 'container' | 'overlay' | 'dialog' | 'title' | 'closeButton';

export type Props = {
	className?: string;
	role?: 'dialog' | 'alertdialog';
	id?: string;
	title?: React.ReactNode;
	dialogRef?: (instance: A11yDialogInstance) => void;
	titleId?: string;
	closeButtonLabel?: string;
	closeButtonContent?: React.ReactNode;
	closeButtonPosition?: 'first' | 'last' | 'none';
	dialogRoot?: string;
	classNames?: Record<A11yDialogClassNames, string>;
	html?: DOMAttributes<HTMLDivElement>['dangerouslySetInnerHTML'];
	disableScroll?: boolean;
	footer?: React.ReactNode;
	rootContent?: React.ReactNode;
	dialogContentContentRef?: React.MutableRefObject<HTMLDivElement | null>;
	maxWrapperHeightToContent?: boolean;
	dataCy?: string | null;
	onHide?: () => void;
};

type DialogEvents = {
	show: EventListener;
	hide: EventListener;
};

export default function Dialog({
	id = `${getUniqueId()}-dialog`,
	className = '',
	role,
	title,
	dialogRef = () => undefined,
	titleId,
	closeButtonLabel,
	closeButtonContent = <Icon icon="close" />,
	closeButtonPosition,
	dialogRoot,
	classNames = {
		container: 'Dialog hidden',
		overlay: 'Dialog-overlay',
		dialog: 'Dialog-wrapper',
		title: 'Dialog-title',
		closeButton: 'Dialog-closebutton',
	},
	children,
	html,
	disableScroll = true,
	footer,
	dialogContentContentRef,
	rootContent,
	maxWrapperHeightToContent = false,
	dataCy,
	onHide = () => undefined,
}: PropsWithChildren<Props>): ReactElement {
	const dialog = useRef<A11yDialogInstance>(undefined);
	const dialogContent = useRef<HTMLDivElement>(null);
	let resizeObserver: ResizeObserver;
	const dialogEventsRef = useRef<DialogEvents>({
		show: noop,
		hide: noop,
	});

	const dialogEvents = (type: 'on' | 'off', instance: A11yDialogInstance) => {
		if (type === 'on') {
			dialogEventsRef.current = {
				show: function (event) {
					const element = event.target as HTMLElement;
					setDialogHeight(window.innerHeight);
					element.classList.remove('hidden');
					if (disableScroll) {
						disableBodyScroll(element.querySelector('.Dialog-content-content'));
					}
					if (maxWrapperHeightToContent && dialog.current && dialogContent.current) {
						const dialogEl: HTMLDivElement = dialog.current['$el'];
						const wrapper = dialogEl.querySelector(
							'.' + classNames.dialog.split(' ').join('.')
						) as HTMLDivElement;
						if (wrapper) {
							const setSize = () => {
								wrapper.style.height = '';
								if (dialogContent.current) {
									wrapper.style.height = `${dialogContent.current.offsetHeight}px`;
								}
							};
							resizeObserver = new ResizeObserver(setSize);
							resizeObserver.observe(dialogEl);
							setSize();
						}
					}
				},
				hide: function (event) {
					const element = event.target as HTMLElement;
					const dialogWrapper = element.querySelector(
						`.${classNames.dialog?.trim().split(/\s/g).join('.')}`
					) as HTMLDivElement;
					let hidden = false;
					const hide = () => {
						onHide();
						hidden = true;
						element.classList.add('hidden');
						if (disableScroll) {
							enableBodyScroll(element.querySelector('.Dialog-content-content'));
						}
						dialogWrapper && dialogWrapper.removeEventListener('transitionend', hide);
					};
					if (dialogWrapper) {
						if (
							getComputedStyle(dialogWrapper).transition?.includes('transform') ||
							!getBreakpoint().includes('large')
						) {
							dialogWrapper.addEventListener('transitionend', hide);
						} else {
							hide();
						}
					} else {
						hide();
					}
					// just in case something odd happens
					setTimeout(() => {
						if (!hidden) {
							hide();
						}
					}, 500);
				},
			};

			instance.on('show', dialogEventsRef.current.show);
			instance.on('hide', dialogEventsRef.current.hide);
		} else {
			instance.off('show', dialogEventsRef.current.show);
			instance.off('hide', dialogEventsRef.current.hide);
		}
	};

	return (
		<A11yDialog
			id={id}
			role={role}
			dialogRef={instance => {
				if (dialog.current) {
					dialogEvents('off', dialog.current);
				}

				dialog.current = instance;
				if (resizeObserver) {
					resizeObserver.disconnect();
				}

				if (dialog.current) {
					dialogEvents('on', dialog.current);
				}

				dialogRef(instance!);
			}}
			title={typeof title === 'string' || title instanceof String ? title : ''}
			titleId={titleId}
			classNames={classNames}
			closeButtonLabel={closeButtonLabel}
			closeButtonPosition={closeButtonPosition}
			dialogRoot={dialogRoot}
			closeButtonContent={closeButtonContent}
		>
			<div ref={dialogContent} className={`Dialog-content ${className}`} data-cy={dataCy}>
				{rootContent}
				{title && (
					<div role="heading" aria-level={1} className="Dialog-content-title ty-c1">
						{title}
					</div>
				)}
				<div className="Dialog-content-content" ref={dialogContentContentRef}>
					<div className="Dialog-html-content" dangerouslySetInnerHTML={html}></div>
					{children}
				</div>
				{footer && (
					<footer aria-level={1} className="Dialog-content-footer">
						{footer}
					</footer>
				)}
			</div>
		</A11yDialog>
	);
}
// I don't love this but safari breaks the css cross browser solution of using fill-available by locking to the smallest variable leaving gaps
let checker: NodeJS.Timeout;
const setDialogHeight = (height: number) =>
	document.documentElement.style.setProperty('--dialogminvh', `${height * 0.01}px`);
window.addEventListener('resize', () => {
	const h = window.innerHeight;
	setDialogHeight(h);
	checker && clearInterval(checker);
	checker = setInterval(() => setDialogHeight(h), 15);
	setTimeout(() => {
		checker && clearInterval(checker);
	}, 1000);
});
